import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader as Loader2, Music, Trophy } from 'lucide-react';
import {
  KioskService,
  getStoredKioskSession,
  KioskLeagueBranding,
  KioskSongEntry,
  resolveLyricsPublicUrl,
} from '../services/KioskService';
import KioskRecorder from '../components/KioskRecorder';
import KioskShell from '../components/KioskShell';
import KioskHeadsetCheck from '../components/KioskHeadsetCheck';
import { useKioskBasePath } from '../hooks/useKioskBasePath';

type Stage = 'record' | 'uploading' | 'scoring' | 'done';

export const KioskSing: React.FC = () => {
  const { slug, songId } = useParams<{ slug: string; songId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testmode = searchParams.get('testmode') === 'true';
  const tmQuery = testmode ? '?testmode=true' : '';
  const basePath = useKioskBasePath();
  const [league, setLeague] = useState<KioskLeagueBranding | null>(null);
  const [song, setSong] = useState<KioskSongEntry | null>(null);
  const [lyrics, setLyrics] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('record');
  const [progressMsg, setProgressMsg] = useState<string>('');
  const [finalScore, setFinalScore] = useState<number | null>(null);
  const [headsetChecked, setHeadsetChecked] = useState(false);
  const [preferredDeviceId, setPreferredDeviceId] = useState<string | undefined>();
  const [initialAudioStream, setInitialAudioStream] = useState<MediaStream | undefined>();
  const [processingStep, setProcessingStep] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!slug || !songId) return;
    const session = getStoredKioskSession();
    if (!session || session.leagueSlug !== slug) {
      navigate(`/${basePath}/${slug}${tmQuery}`);
      return;
    }
    setDisplayName(session.displayName);
    (async () => {
      try {
        setLoading(true);
        const [lb, songs, meData] = await Promise.all([
          KioskService.getLeaderboard(slug),
          KioskService.getLeagueSongs(slug),
          KioskService.getMe(),
        ]);

        const meRec = meData as Record<string, any> | null;
        const hasSubmitted = meRec?.submissions && meRec.submissions.length > 0;
        if (hasSubmitted) {
          navigate(`/${basePath}/${slug}/me${tmQuery}`);
          return;
        }

        setLeague(lb.league);
        const found = songs.find((s) => s.id === songId) || null;
        setSong(found);

        if (found?.lyrics_url) {
          const url = resolveLyricsPublicUrl(found.lyrics_url);
          if (url) {
            try {
              const res = await fetch(url);
              if (res.ok) setLyrics(await res.text());
            } catch {
              // lyrics are optional
            }
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    };
  }, [slug, songId, navigate]);

  const handleRecordingComplete = async (blob: Blob, durationSec: number, recordingType: 'audio' | 'video') => {
    if (!song || !slug) return;
    setError(null);
    setStage('uploading');
    setProcessingStep(0);
    setProgressMsg('Uploading your take...');
    try {
      await KioskService.joinQueue().catch(() => { /* best-effort */ });
      setProgressMsg('Uploading your take...');
      const res = await KioskService.submitRecording({
        blob,
        songId: song.id,
        leagueSlug: slug,
        durationSeconds: durationSec,
        recordingType,
        testmode,
      });

      setStage('scoring');
      setProcessingStep(1);
      setProgressMsg(testmode ? 'Processing & scoring your vocals...' : 'Extracting your vocals...');

      // Advance steps on a timer to give visual feedback
      if (!testmode) {
        stepTimerRef.current = setTimeout(() => {
          setProcessingStep(2);
          setProgressMsg('Processing audio...');
        }, 8000);
      }

      const started = Date.now();
      const maxMs = 180_000;
      pollRef.current = setInterval(async () => {
        try {
          const elapsed = Date.now() - started;
          if (!testmode && elapsed > 20000 && processingStep < 3) {
            setProcessingStep(3);
            setProgressMsg('Scoring your performance...');
          }
          if (testmode && elapsed > 10000) {
            setProcessingStep(2);
            setProgressMsg('Scoring your performance...');
          }

          const status = await KioskService.getSubmissionStatus(res.submission_id);
          if (status && status.score != null) {
            if (pollRef.current) clearInterval(pollRef.current);
            if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
            const scoreNum = Number(status.score);
            setFinalScore(scoreNum);
            if (scoreNum === 0) {
              setError('Your take scored 0 - the audio may have been silent or the scoring service could not analyze it. Please contact league staff if this looks wrong.');
            }
            setStage('done');
            return;
          }
          if (elapsed > maxMs) {
            if (pollRef.current) clearInterval(pollRef.current);
            if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
            setError('Scoring is taking longer than expected. Your take has been submitted - check the leaderboard shortly.');
            setStage('done');
          }
        } catch (pollErr) {
          if (pollRef.current) clearInterval(pollRef.current);
          if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
          setError(pollErr instanceof Error ? pollErr.message : 'Failed to check score');
          setStage('done');
        }
      }, 1000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to submit';
      if (msg.toLowerCase().includes('already submitted')) {
        setError('You have already submitted a take for this league.');
        setTimeout(() => navigate(`/${basePath}/${slug}/me${tmQuery}`), 1500);
      } else {
        setError(msg);
        setStage('record');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!league || !song) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80">
        Song not available.
      </div>
    );
  }

  const accent = league.accent_color || '#f97316';
  const primary = league.primary_color || '#f97316';

  return (
    <KioskShell league={league} displayName={displayName} leagueSlug={slug!}>
      {error && (
        <div className="mb-4 p-3 bg-red-500/15 border border-red-400/30 rounded-lg text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="mb-4">
        <Link
          to={`/${basePath}/${slug}/me${tmQuery}`}
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Back to my page
        </Link>
      </div>

      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-6 sm:p-8">
        <div className="text-center mb-6">
          {song.cover_url ? (
            <img src={song.cover_url} alt={song.title} className="w-24 h-24 mx-auto rounded-xl object-cover mb-3 shadow-lg" />
          ) : (
            <div className="w-24 h-24 mx-auto rounded-xl bg-white/10 flex items-center justify-center mb-3">
              <Music className="w-10 h-10 text-white/50" />
            </div>
          )}
          <h1 className="text-2xl font-black">{song.title}</h1>
          <p className="text-white/60">{song.artist}</p>
        </div>

        {stage === 'record' && !headsetChecked && (
          <KioskHeadsetCheck
            accentColor={accent}
            primaryColor={primary}
            onContinue={(deviceId, liveStream) => {
              setPreferredDeviceId(deviceId);
              setInitialAudioStream(liveStream);
              setHeadsetChecked(true);
            }}
          />
        )}

        {stage === 'record' && headsetChecked && (
          <KioskRecorder
            backingTrackUrl={song.audio_url}
            lyricsContent={lyrics}
            maxSeconds={120}
            preferredDeviceId={preferredDeviceId}
            initialAudioStream={initialAudioStream}
            accentColor={accent}
            onComplete={handleRecordingComplete}
            onError={(msg) => setError(msg)}
          />
        )}

        {(stage === 'uploading' || stage === 'scoring') && (
          <div className="text-center py-10">
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: accent }} />
            <p className="text-lg font-semibold">{progressMsg}</p>
            <p className="text-sm text-white/50 mt-2">This can take a few minutes.</p>
            <div className="mt-6 max-w-xs mx-auto">
              <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-white/10">
                {(testmode ? [0, 1, 2] : [0, 1, 2, 3]).map((i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-full transition-all duration-700"
                    style={{
                      backgroundColor: i < processingStep ? accent : i === processingStep ? accent : 'transparent',
                      opacity: i < processingStep ? 1 : i === processingStep ? 0.5 : 0,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {stage === 'done' && (
          <div className="text-center py-8">
            <Trophy className="w-14 h-14 mx-auto mb-4" style={{ color: accent }} />
            {finalScore != null ? (
              <>
                <p className="text-white/70 mb-1">Your score</p>
                <div className="text-6xl font-black mb-6" style={{ color: accent }}>
                  {finalScore.toFixed(1)}
                </div>
              </>
            ) : (
              <p className="text-white/80 mb-6">Submission received.</p>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <Link
                to={`/${basePath}/${slug}/me${tmQuery}`}
                className="px-5 py-3 rounded-xl font-semibold text-white hover:brightness-110 transition shadow-lg"
                style={{ backgroundColor: primary }}
              >
                View My Results
              </Link>
              <Link
                to={`/${basePath}/${slug}/leaderboard${tmQuery}`}
                className="px-5 py-3 rounded-xl font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/15"
              >
                Leaderboard
              </Link>
            </div>
          </div>
        )}
      </div>
    </KioskShell>
  );
};

export default KioskSing;
